'use client'

import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { Loader2, Pause, Play, RotateCcw, RotateCw } from 'lucide-react'
import { Slider as SliderPrimitive } from 'radix-ui'
import { toast } from '@/hooks/use-toast'
import { Button } from '@/components/ui/button'
import { useCallRecordingAudio } from '@/features/cases/hooks/use-call-recording-audio'
import { AppError } from '@/lib/errors/appError'
import { cn } from '@/lib/utils'

const SKIP_SECONDS = 10

function formatSeconds(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return '0:00'
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = Math.floor(totalSeconds % 60)
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

/** Imperative handle exposed to a parent (e.g. `CallDetailDialog`) so a transcript line can drive
 * playback — this component otherwise owns all of its own state and a parent has no other way in. */
export interface CallRecordingPlayerHandle {
  /** Lazily loads the audio if playback hasn't started yet (same `ensureLoaded()` path `skip`/
   * `onSeek` already use), then jumps to `seconds` and plays from there. */
  seekTo: (seconds: number) => Promise<void>
}

/**
 * Full playback control for the call-detail dialog, laid out Spotify-style: the seek bar sits on
 * top, playback controls (rewind 10s / play-pause / forward 10s) sit centered below it. `src` is
 * fetched lazily (on first play) via the backend's authenticated proxy, never a raw Plivo URL,
 * cached as an object URL for the life of this component and revoked on unmount — same sourcing as
 * the compact `<CallRecording>` control used in the collapsed card view.
 */
export const CallRecordingPlayer = forwardRef<CallRecordingPlayerHandle, { callId: string }>(
  function CallRecordingPlayer({ callId }, ref) {
    const { fetchAudioUrl } = useCallRecordingAudio()
    const audioRef = useRef<HTMLAudioElement | null>(null)
    const trackRef = useRef<HTMLSpanElement | null>(null)
    const [src, setSrc] = useState<string | null>(null)
    const [loading, setLoading] = useState(false)
    const [playing, setPlaying] = useState(false)
    const [currentTime, setCurrentTime] = useState(0)
    const [duration, setDuration] = useState(0)
    const [hoverFraction, setHoverFraction] = useState<number | null>(null)

    useEffect(() => {
      const audio = audioRef.current
      if (!audio) return
      const onEnded = () => setPlaying(false)
      const onTimeUpdate = () => setCurrentTime(audio.currentTime)
      const onLoadedMetadata = () => setDuration(audio.duration)
      const onDurationChange = () => setDuration(audio.duration)
      audio.addEventListener('ended', onEnded)
      audio.addEventListener('timeupdate', onTimeUpdate)
      audio.addEventListener('loadedmetadata', onLoadedMetadata)
      audio.addEventListener('durationchange', onDurationChange)
      return () => {
        audio.removeEventListener('ended', onEnded)
        audio.removeEventListener('timeupdate', onTimeUpdate)
        audio.removeEventListener('loadedmetadata', onLoadedMetadata)
        audio.removeEventListener('durationchange', onDurationChange)
      }
    }, [])

    useEffect(() => {
      return () => {
        if (src) URL.revokeObjectURL(src)
      }
    }, [src])

    const ensureLoaded = async (): Promise<boolean> => {
      if (src) return true
      setLoading(true)
      try {
        const url = await fetchAudioUrl(callId)
        setSrc(url)
        return true
      } catch (error) {
        toast({ variant: 'destructive', title: AppError.fromUnknown(error).message })
        return false
      } finally {
        setLoading(false)
      }
    }

    const togglePlay = async () => {
      const audio = audioRef.current
      if (!audio) return

      if (playing) {
        audio.pause()
        setPlaying(false)
        return
      }

      const ready = src ? true : await ensureLoaded()
      if (!ready) return
      // `src` only takes effect on the next render — play once the element actually has it.
      requestAnimationFrame(() => {
        void audioRef.current?.play()
        setPlaying(true)
      })
    }

    const skip = async (deltaSeconds: number) => {
      const audio = audioRef.current
      const ready = src ? true : await ensureLoaded()
      if (!ready || !audio) return
      requestAnimationFrame(() => {
        const el = audioRef.current
        if (!el) return
        const next = Math.min(Math.max(el.currentTime + deltaSeconds, 0), el.duration || 0)
        el.currentTime = next
        setCurrentTime(next)
      })
    }

    const fractionFromClientX = (clientX: number): number | null => {
      const track = trackRef.current
      if (!track || !duration) return null
      const rect = track.getBoundingClientRect()
      const fraction = (clientX - rect.left) / rect.width
      return Math.min(1, Math.max(0, fraction))
    }

    const onSeek = async ([value]: number[]): Promise<void> => {
      const audio = audioRef.current
      const ready = src ? true : await ensureLoaded()
      if (!ready || !audio || value === undefined) return
      audio.currentTime = value
      setCurrentTime(value)
    }

    const seekTo = async (seconds: number): Promise<void> => {
      const ready = src ? true : await ensureLoaded()
      if (!ready) return
      // `src` only takes effect on the next render (same reasoning as `togglePlay`) — apply the seek
      // and start playback once the element actually has it.
      requestAnimationFrame(() => {
        const el = audioRef.current
        if (!el) return
        el.currentTime = seconds
        setCurrentTime(seconds)
        void el.play()
        setPlaying(true)
      })
    }

    useImperativeHandle(ref, () => ({ seekTo }))

    return (
      <div className="flex flex-col gap-1">
        <div className="flex flex-col gap-1">
          <span
            ref={trackRef}
            className="relative block py-1"
            onMouseMove={(e) => setHoverFraction(fractionFromClientX(e.clientX))}
            onMouseLeave={() => setHoverFraction(null)}
          >
            {hoverFraction !== null && duration > 0 && (
              <div
                className="bg-popover text-popover-foreground pointer-events-none absolute -top-6 -translate-x-1/2 rounded border px-1.5 py-0.5 text-xs shadow-sm"
                style={{ left: `${hoverFraction * 100}%` }}
              >
                {formatSeconds(hoverFraction * duration)}
              </div>
            )}
            <SliderPrimitive.Root
              className="relative flex w-full touch-none select-none items-center"
              min={0}
              max={duration || 0}
              step={0.1}
              value={[Math.min(currentTime, duration || 0)]}
              onValueChange={(value) => void onSeek(value)}
              aria-label="Seek recording"
            >
              <SliderPrimitive.Track className="bg-muted relative h-1.5 w-full grow overflow-hidden rounded-full">
                <SliderPrimitive.Range className="bg-primary absolute h-full rounded-full" />
              </SliderPrimitive.Track>
              <SliderPrimitive.Thumb className="border-primary bg-background focus-visible:ring-ring/50 block size-4 shrink-0 rounded-full border-2 shadow transition-[box-shadow] hover:scale-110 focus-visible:outline-none focus-visible:ring-4 disabled:pointer-events-none disabled:opacity-50" />
            </SliderPrimitive.Root>
          </span>
          <div className="text-muted-foreground flex justify-between text-xs tabular-nums">
            <span>{formatSeconds(currentTime)}</span>
            <span>{formatSeconds(duration)}</span>
          </div>
        </div>

        <div className="flex items-center justify-center gap-3">
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="relative size-8"
            onClick={() => void skip(-SKIP_SECONDS)}
            disabled={loading}
            aria-label={`Rewind ${SKIP_SECONDS} seconds`}
          >
            <RotateCcw className="size-4" />
            <span className="pointer-events-none absolute inset-0 flex items-center justify-center pt-[2px] text-[7px] font-semibold">
              {SKIP_SECONDS}
            </span>
          </Button>

          <Button
            type="button"
            size="icon"
            className="size-10 rounded-full shadow-sm"
            onClick={() => void togglePlay()}
            disabled={loading}
            aria-label={playing ? 'Pause recording' : 'Play recording'}
          >
            {loading ? (
              <Loader2 className="size-4 animate-spin" />
            ) : playing ? (
              <Pause className={cn('size-4 fill-current')} />
            ) : (
              <Play className="size-4 fill-current pl-0.5" />
            )}
          </Button>

          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="relative size-8"
            onClick={() => void skip(SKIP_SECONDS)}
            disabled={loading}
            aria-label={`Forward ${SKIP_SECONDS} seconds`}
          >
            <RotateCw className="size-4" />
            <span className="pointer-events-none absolute inset-0 flex items-center justify-center pt-[2px] text-[7px] font-semibold">
              {SKIP_SECONDS}
            </span>
          </Button>
        </div>

        {/* Always rendered (even with no src yet) — `togglePlay`/`onSeek`/`skip` need `audioRef.current`
         * to exist before `fetchAudioUrl` has anything to set as `src`. */}
        <audio ref={audioRef} src={src ?? undefined} preload="none" />
      </div>
    )
  },
)
